import {FC, PropsWithChildren, ReactNode} from 'react';
import {type IBrand} from '@verdocs/js-sdk';
import {XMarkIcon} from '@heroicons/react/24/outline';
import {brandInitials, brandTileColor} from './BrandThemes';

interface BuilderFrameProps {
  brand: IBrand;
  name: string;
  logoUrl?: string | null;
  label: string;
  dirty: boolean;
  working: boolean;
  onClose: () => void;
  onDiscard: () => void;
  onPublish: () => void;
  onSaveDraft?: () => void;
  // Builder-specific controls that sit in the bar ahead of the status pill.
  tools?: ReactNode;
}

// The builders take over the whole window, under the same title bar the brand page uses, so the
// preview gets the width the nav would otherwise hold.
export const BuilderFrame: FC<PropsWithChildren<BuilderFrameProps>> = ({
  brand,
  name,
  logoUrl,
  label,
  dirty,
  working,
  onClose,
  onDiscard,
  onPublish,
  onSaveDraft,
  tools,
  children,
}) => (
  <div className="fixed inset-0 z-40 flex flex-col bg-white">
    <div className="border-b border-gray-200 py-3.5">
      <div className="flex flex-wrap items-center gap-3 px-4 laptop:px-6">
        {logoUrl ? (
          <img src={logoUrl} alt="" className="h-8 w-8 rounded-lg bg-gray-50 object-contain" />
        ) : (
          <div
            className="flex h-8 w-8 items-center justify-center rounded-lg text-[13px] font-extrabold text-white"
            style={{background: brandTileColor(brand)}}>
            {brandInitials(name, brand.key)}
          </div>
        )}
        <h1 className="text-[17px] font-semibold text-gray-900">{name || brand.key}</h1>
        <span className="font-mono text-[11px] text-gray-400">{brand.key}</span>
        <span className="text-[13px] text-gray-400">{label}</span>
        <div className="flex-1" />
        {tools}
        <span
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${
            dirty ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'
          }`}>
          <i className={`h-[7px] w-[7px] rounded-full ${dirty ? 'bg-amber-500' : 'bg-emerald-500'}`} />
          {dirty ? 'Draft · unpublished changes' : 'Published · in sync'}
        </span>
        {dirty && (
          <>
            <button onClick={onDiscard} className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-500 hover:bg-gray-100">
              Discard
            </button>
            {onSaveDraft && (
              <button
                onClick={onSaveDraft}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">
                Save draft
              </button>
            )}
          </>
        )}
        <button
          onClick={onPublish}
          disabled={!dirty || working}
          className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-40">
          Publish
        </button>
        <button onClick={onClose} className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50" title="Close">
          <XMarkIcon className="size-4" />
        </button>
      </div>
    </div>

    <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-4 laptop:p-6">{children}</div>
  </div>
);

interface LeaveDialogProps {
  onStay: () => void;
  onLeave: () => void;
  // Offered only where drafts exist (the Theme Editor).
  onSaveAndLeave?: () => void;
}

/** Shown when a builder is closed, or left by a link, with changes that would be lost. */
export const LeaveDialog: FC<LeaveDialogProps> = ({onStay, onLeave, onSaveAndLeave}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-6" onClick={(e) => e.target === e.currentTarget && onStay()}>
    <div className="w-[460px] max-w-full rounded-2xl bg-white p-6 shadow-2xl">
      <h3 className="mb-2 text-[15.5px] font-semibold text-gray-900">Unsaved changes</h3>
      <p className="mb-6 text-sm leading-relaxed text-gray-600">
        {onSaveAndLeave
          ? 'Save your changes as a draft to pick up where you left off, or leave without them.'
          : 'Your changes have not been published. Leaving now drops them.'}
      </p>
      <div className="flex flex-wrap justify-end gap-2">
        <button onClick={onLeave} className="rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">
          Discard changes
        </button>
        <button
          onClick={onStay}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50">
          Keep editing
        </button>
        {onSaveAndLeave && (
          <button onClick={onSaveAndLeave} className="rounded-lg bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">
            Save draft and leave
          </button>
        )}
      </div>
    </div>
  </div>
);
